/**
 * high5WeeklyMailProse.js
 *
 * High5 haftalık mailinin YAZI kısmı. `/api/high5-weekly-mail` yanıtını alır,
 * mailde okunacak paragrafları döndürür. Saf modül — JSX yok, React yok,
 * ölçülebilsin ve testte çağrılabilsin diye.
 *
 * 🔴 KURAL: yalnız ölçülmüş olan yazılır. "Şu yüzden arttı", "bilerek yaptık",
 * "bu hafta şunu yapacağız" gibi SEBEP ve NİYET cümleleri buradan ÇIKMAZ;
 * onlar sayfadaki elle düzeltilen paragraflara aittir.
 * (Bkz. feedback: veri yoksa yorum yok.)
 *
 * 🔑 Türkçe ek sorunu: sayıdan/özel addan sonra ek getirmiyoruz ("%66'sı",
 * "ON'un" gibi biçimler kesme işaretiyle patlıyor). Cümleler eki gerektirmeyecek
 * şekilde kuruluyor ya da "→" kullanılıyor.
 */

import { DATA as MTD } from './high5MtdData';

const GUN = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const ymdAdd = (ymd, n) => {
  const t = new Date(`${ymd}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};
/** MTD dizisinden bir günün satırı: { tRev, aRev, tTrx, aTrx } ya da null. */
const mtdGun = (ymd) => {
  const r = (MTD[ymd.slice(0, 7)] || []).find((x) => x[0] === Number(ymd.slice(8, 10)));
  return r ? { tRev: r[1], aRev: r[2], tTrx: r[3], aTrx: r[4] } : null;
};

/**
 * 🔑 19 Eki (Efe): hedef bölümünün rakamları /high5/mtd-hedef ile AYNI olmalı —
 * müşterinin günlük hedef Excel'i (utils/high5MtdData.js). API'nin BQ export
 * cirosu Excel'in altında kalıyordu (12–18 Eki 3.448.777 ↔ 3.769.842).
 * Haftanın 7 günü de kapanmış değilse null → API rakamlarına düşülür.
 */
function statikHafta(start) {
  const gunler = Array.from({ length: 7 }, (_, i) => ymdAdd(start, i));
  const satir = gunler.map(mtdGun);
  if (satir.some((r) => !r || r.aRev == null)) return null;
  const top = (rs, k) => rs.reduce((a, r) => a + (r?.[k] || 0), 0);
  const onceki = Array.from({ length: 7 }, (_, i) => mtdGun(ymdAdd(start, i - 7)));
  const prev = onceki.every((r) => r && r.aRev != null) ? top(onceki, 'aRev') : null;
  const actual = top(satir, 'aRev');
  const target = top(satir, 'tRev');

  // Ay: haftanın bittiği ay, o güne kadar kapanmış günler — mtd-hedef'teki gibi
  // ay sonu tahmini = günlük ortalama × ayın gün sayısı.
  const son = gunler[6];
  const ay = MTD[son.slice(0, 7)] || [];
  const kapanan = ay.filter((r) => r[0] <= Number(son.slice(8, 10)) && r[2] != null);
  const actualToDate = kapanan.reduce((a, r) => a + r[2], 0);
  const targetToDate = kapanan.reduce((a, r) => a + r[1], 0);
  const AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  return {
    revenue: {
      target, actual, prev,
      chg: prev ? ((actual - prev) / prev) * 100 : null,
      transactions: {
        cur: top(satir, 'aTrx'),
        target: top(satir, 'tTrx'),
        hgo: top(satir, 'tTrx') ? (top(satir, 'aTrx') / top(satir, 'tTrx')) * 100 : null,
      },
    },
    month: {
      label: `${AY[Number(son.slice(5, 7)) - 1]} ${son.slice(0, 4)}`,
      daysCounted: kapanan.length,
      actualToDate, targetToDate,
      gap: actualToDate - targetToDate,
      forecast: kapanan.length ? Math.round((actualToDate / kapanan.length) * ay.length) : null,
      monthTarget: ay.reduce((a, r) => a + r[1], 0),
    },
    days: gunler.map((g, i) => ({
      date: g,
      dowLabel: GUN[new Date(`${g}T00:00:00Z`).getUTCDay()],
      actual: { revenue: satir[i].aRev },
      hgo: { revenue: satir[i].tRev ? (satir[i].aRev / satir[i].tRev) * 100 : null },
    })),
  };
}

const nf = (n, d = 0) => (n == null || Number.isNaN(Number(n)) ? '—'
  : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d }));
const money = (n, d = 0) => (n == null ? '—' : `${nf(n, d)} ₺`);
const pctv = (n, d = 1) => (n == null ? '—' : `%${nf(n, d)}`);
const dm = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : '');

/** −%12,4 / +%12,4 — işaret cümlede okunsun diye ayrı. */
const chgTxt = (n) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}%${nf(Math.abs(n), 1)}`);
/** "arttı / geriledi / yatay kaldı" — %0,5 altı değişimi hareket saymıyoruz. */
const yon = (n, artti = 'arttı', azaldi = 'geriledi', yatay = 'yatay kaldı') => {
  if (n == null) return yatay;
  if (Math.abs(n) < 0.5) return yatay;
  return n > 0 ? artti : azaldi;
};
const fark = (n, f = money) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${f(Math.abs(n))}`);

/**
 * @param {object} d  /api/high5-weekly-mail yanıtı
 * @param {object} [ops]
 * @param {number} [ops.musteriCirosu]  müşterinin kendi tablosundaki ciro (varsa giriş cümlesi onunla)
 * @returns {Array<{id,baslik,paragraflar:string[],shot:string|null,notYeri:string|null}>}
 */
export function buildProse(d, ops = {}) {
  if (!d) return [];
  const hafta = `${d.week.isoWeek}. hafta (${dm(d.week.start)} – ${dm(d.week.end)})`;
  const st = statikHafta(d.week.start);
  if (st) d = { ...d, revenue: st.revenue, month: st.month, days: st.days };
  const musteri = Number(ops.musteriCirosu);
  const ciro = Number.isFinite(musteri) && musteri > 0 ? musteri : d.revenue.actual;
  const hgo = d.revenue.target ? (ciro / d.revenue.target) * 100 : null;
  const S = [];

  /* 1 ─ hafta ve ay ────────────────────────────────────────────────────────── */
  {
    const p = [];
    // Başlıkta hafta zaten yazıyor, cümlede tekrarlamıyoruz.
    p.push(
      `Haftayı ${money(d.revenue.target)} ciro hedefiyle açtık, ${money(ciro)} ile kapattık — `
      + `hedef gerçekleşme oranı ${pctv(hgo, 0)}. Bir önceki haftaya göre ciro ${chgTxt(d.revenue.chg)} `
      + `(${money(d.revenue.prev)} → ${money(d.revenue.actual)}), işlem adedi ${nf(d.revenue.transactions.cur)} `
      + `(hedef ${nf(d.revenue.transactions.target)}, gerçekleşme ${pctv(d.revenue.transactions.hgo, 0)}).`,
    );
    if (d.month.targetToDate) {
      p.push(
        `${d.month.label} genelinde ${d.month.daysCounted} günde ${money(d.month.actualToDate)} yaptık; `
        + `aynı güne kadarki hedef ${money(d.month.targetToDate)}, yani `
        + `${d.month.gap < 0 ? `${money(Math.abs(d.month.gap))} gerideyiz` : `${money(Math.abs(d.month.gap))} öndeyiz`}. `
        + `Bu tempo ay sonuna kadar sürerse ay ${money(d.month.forecast)} ile kapanır, ay hedefi ${money(d.month.monthTarget)}.`,
      );
    }
    // En iyi ve en kötü gün — tabloyu okumadan da anlaşılsın.
    const dolu = (d.days || []).filter((x) => x.actual && x.hgo);
    if (dolu.length) {
      const enIyi = dolu.reduce((a, b) => (b.hgo.revenue > a.hgo.revenue ? b : a));
      const enKotu = dolu.reduce((a, b) => (b.hgo.revenue < a.hgo.revenue ? b : a));
      p.push(
        `Haftanın en güçlü günü ${enIyi.dowLabel} (${dm(enIyi.date)}): ${money(enIyi.actual.revenue)}, `
        + `hedefin ${pctv(enIyi.hgo.revenue, 0)} düzeyinde. En zayıf gün ${enKotu.dowLabel} (${dm(enKotu.date)}): `
        + `${money(enKotu.actual.revenue)}, hedefin ${pctv(enKotu.hgo.revenue, 0)} düzeyinde.`,
      );
    }
    S.push({ id: 'hafta', baslik: `${hafta} — hedef ve gerçekleşen`, paragraflar: p, shot: 'hedef-takip', notYeri: 'acilis' });
  }

  /* 2 ─ trafik ve dönüşüm ──────────────────────────────────────────────────── */
  {
    const t = d.traffic;
    const ayiklanan = (t.excluded || []).reduce((a, x) => a + (x.cur || 0), 0);
    const ayiklananOnce = (t.excluded || []).reduce((a, x) => a + (x.prev || 0), 0);
    const p = [];
    p.push(
      `Toplam oturum ${nf(t.sessionsAll.cur)} — önceki haftaya göre ${chgTxt(t.sessionsAll.chg)}. `
      + `Bu rakamın içinde RTB ve medya alımı kaynaklı ${nf(ayiklanan)} oturum var `
      + `(önceki hafta ${nf(ayiklananOnce)}); tık garantili envanterden gelen bu trafik oturumu yukarı çekiyor `
      + `ama işleme dönüşmüyor.`,
    );
    p.push(
      `Onu ayıkladığımızda performans tabanındaki oturum ${nf(t.sessionsPure.cur)} → ${chgTxt(t.sessionsPure.chg)}, `
      + `yani trafik ${yon(t.sessionsPure.chg)}. Aynı tabanda dönüşüm oranı ${pctv(t.crPure.cur, 2)} `
      + `(önceki hafta ${pctv(t.crPure.prev, 2)}). Ham oturum üzerinden bakıldığında ise CR ${pctv(t.crAll.cur, 2)} görünüyor — `
      + `iki rakam arasındaki fark tamamen bu ayıklamadan geliyor.`,
    );
    p.push(
      `İşlem adedi ${nf(t.transactions.cur)} (${chgTxt(t.transactions.chg)}), AEM tarafındaki ciro `
      + `${money(t.revenue.cur)} (${chgTxt(t.revenue.chg)}).`,
    );
    S.push({ id: 'trafik', baslik: 'Trafik ve dönüşüm', paragraflar: p, shot: 'haftalik-kpi', notYeri: 'trafik' });
  }

  /* 3 ─ harcama ────────────────────────────────────────────────────────────── */
  {
    const sp = d.spend.total;
    const p = [];
    const platf = (d.spend.byPlatform || []).filter((x) => x.cost.cur > 0 || x.cost.prev > 0);
    const artan = platf.filter((x) => (x.cost.diff || 0) > 0).sort((a, b) => b.cost.diff - a.cost.diff);
    const azalan = platf.filter((x) => (x.cost.diff || 0) < 0).sort((a, b) => a.cost.diff - b.cost.diff);

    p.push(
      `Haftanın toplam harcaması ${money(sp.cost.cur)}, önceki haftaya göre ${fark(sp.cost.diff)} (${chgTxt(sp.cost.chg)}). `
      + (artan.length
        ? `Artış ${artan.slice(0, 2).map((x) => `${x.platform} ${fark(x.cost.diff)}`).join(', ')} tarafında. `
        : '')
      + (azalan.length
        ? `Geri çekilen taraf: ${azalan.slice(0, 2).map((x) => `${x.platform} ${fark(x.cost.diff)}`).join(', ')}.`
        : ''),
    );
    p.push(
      `Tık sayısı ${nf(sp.clicks.cur)} (${fark(sp.clicks.diff, nf)}), ortalama tık maliyeti ${money(sp.cpc.cur, 2)} `
      + `(${fark(sp.cpc.diff, (v) => money(v, 2))}). Ortalama TBM bütün platformların ortalaması olduğu için bir platform `
      + `durduğunda ya da başladığında tık maliyetleri değişmeden de sıçrar; platform satırları esas alınmalı.`,
    );
    if (sp.roas.cur != null) {
      p.push(`Haftanın toplam getirisi ${nf(sp.roas.cur, 2)} ROAS (önceki hafta ${nf(sp.roas.prev, 2)}).`);
    }
    S.push({ id: 'harcama', baslik: 'Harcama', paragraflar: p, shot: null, notYeri: 'harcama' });
  }

  /* 4 ─ marka bazında reklam ───────────────────────────────────────────────── */
  {
    const markalar = (d.spend.byBrand || []).slice(0, 5);
    const p = [];
    for (const m of markalar.slice(0, 3)) {
      const g = m.google;
      const parca = [
        `${m.brand}: toplam ${money(m.cost.cur)} (${fark(m.cost.diff)})`,
        `Google tarafında ${money(g.cost.cur)} ve ${nf(g.clicks.cur)} tık (${fark(g.clicks.diff, nf)})`,
        `TBM ${money(g.cpc.cur, 2)} (${fark(g.cpc.diff, (v) => money(v, 2))})`,
      ];
      if (m.impressionShare.cur != null) {
        parca.push(
          `arama gösterim payı ${pctv(m.impressionShare.cur)}`
          + (m.impressionShare.prev != null ? ` (önceki hafta ${pctv(m.impressionShare.prev)})` : ''),
        );
      }
      if (m.meta.cost.cur > 0) parca.push(`Meta tarafında ${money(m.meta.cost.cur)} ve ${nf(m.meta.clicks.cur)} tık`);
      p.push(`${parca.join(', ')}.`);
    }
    const kalan = markalar.slice(3);
    if (kalan.length) {
      p.push(
        `Diğerleri: ${kalan.map((m) => `${m.brand} ${money(m.cost.cur)} (${fark(m.cost.diff)})`).join(', ')}.`,
      );
    }
    p.push(
      'Gösterim payı Google Ads tablosunda hazır bir sütun olarak gelmiyor; sıralama ve bütçe kaybı '
      + 'çıkarılarak hesaplanıyor ve yalnız arama kampanyalarından alınıyor — PMAX satırlarında bu metrik boş geliyor.',
    );
    S.push({ id: 'markalar', baslik: 'Marka bazında reklam', paragraflar: p, shot: 'acik-artirma', notYeri: 'markalar' });
  }

  /* 5 ─ paid / unpaid ──────────────────────────────────────────────────────── */
  {
    const paid = (d.channels || []).filter((c) => c.family === 'Paid');
    const unpaid = (d.channels || []).filter((c) => c.family === 'Unpaid');
    const topla = (arr, f) => arr.reduce((a, c) => a + (f(c) || 0), 0);
    const pRev = topla(paid, (c) => c.revenue.cur);
    const pRevP = topla(paid, (c) => c.revenue.prev);
    const uRev = topla(unpaid, (c) => c.revenue.cur);
    const uRevP = topla(unpaid, (c) => c.revenue.prev);
    const oran = (a, b) => (b > 0 ? Math.round(((a - b) / b) * 1000) / 10 : null);
    const enler = [...(d.channels || [])].sort((a, b) => b.revenue.cur - a.revenue.cur).slice(0, 4);
    const p = [];
    p.push(
      `Paid kanallar ${money(pRev)} ciro getirdi (${chgTxt(oran(pRev, pRevP))}), unpaid taraf ${money(uRev)} `
      + `(${chgTxt(oran(uRev, uRevP))}). Cironun ${pctv(pRev + uRev > 0 ? (pRev / (pRev + uRev)) * 100 : null, 0)} `
      + `kısmı paid kanallardan geliyor.`,
    );
    p.push(
      `En büyük dört kanal: ${enler.map((c) => `${c.channel} ${money(c.revenue.cur)} (${chgTxt(c.revenue.chg)})`).join(', ')}.`,
    );
    S.push({ id: 'kanallar', baslik: 'Paid / unpaid kırılımı', paragraflar: p, shot: null, notYeri: 'kanallar' });
  }

  /* 6 ─ organik ────────────────────────────────────────────────────────────── */
  {
    const o = d.organic;
    const sc = o.searchConsole;
    const p = [];
    p.push(
      `Organik aramadan gelen oturum ${nf(o.sessions.cur)} (${chgTxt(o.sessions.chg)}), yani trafik `
      + `${yon(o.sessions.chg)}. Buna karşılık organik ciro ${money(o.revenue.cur)} (${chgTxt(o.revenue.chg)}) ve `
      + `işlem adedi ${nf(o.transactions.cur)} → önceki hafta ${nf(o.transactions.prev)}.`,
    );
    p.push(
      `Search Console tarafında toplam tık ${nf(sc.clicks.cur)} (${chgTxt(sc.clicks.chg)}). Bunun `
      + `${nf(sc.home.cur)} tanesi ana sayfaya (${chgTxt(sc.home.chg)}), ${nf(sc.rest.cur)} tanesi ana sayfa dışındaki `
      + `sayfalara (${chgTxt(sc.rest.chg)}) geliyor; ana sayfanın payı ${pctv(sc.homeShare)}.`,
    );
    if (sc.complete === false) {
      p.push(
        `⚠ Search Console verisi bu hafta için tamamlanmamış: son veri günü ${dm(sc.lastDateInWeek)}, hafta `
        + `${dm(d.week.end)} bitiyor. Yukarıdaki organik tık rakamları eksik, birkaç gün sonra yukarı düzelecek.`,
      );
    }
    S.push({ id: 'organik', baslik: 'Organik arama', paragraflar: p, shot: null, notYeri: 'organik' });
  }

  /* 7 ─ marka arama kampanyası ─────────────────────────────────────────────── */
  {
    const b = d.brandCampaign;
    const is = b.impressionShare;
    const p = [];
    const puan = (is.cur != null && is.prev != null) ? Math.round((is.cur - is.prev) * 10) / 10 : null;
    p.push(
      `Markanın kendi adına kurulu arama kampanyasında gösterim payı ${pctv(is.cur)}`
      + (is.prev != null ? `, önceki hafta ${pctv(is.prev)} idi (${puan > 0 ? '+' : puan < 0 ? '−' : ''}${nf(Math.abs(puan || 0), 1)} puan)` : '')
      + `. Bütçe yetersizliğinden kaybedilen pay ${pctv(is.budgetLostCur)}.`,
    );
    p.push(
      `Kampanya ${nf(b.impressions.cur)} gösterim (${chgTxt(b.impressions.chg)}) ve ${nf(b.clicks.cur)} tık `
      + `(${chgTxt(b.clicks.chg)}) aldı, maliyeti ${money(b.cost.cur)} (${chgTxt(b.cost.chg)}), TBM ${money(b.cpc.cur, 2)}.`,
    );
    S.push({ id: 'marka-kampanya', baslik: 'Marka arama kampanyası', paragraflar: p, shot: 'marka-kapsama', notYeri: 'marka_kampanya' });
  }

  /* 8 ─ ERP net satış ──────────────────────────────────────────────────────── */
  if (d.netSales?.available) {
    const rows = (d.netSales.brands || []).filter((r) => r.amount.cur !== 0 || r.amount.prev !== 0);
    const artan = rows.filter((r) => (r.amount.diff || 0) > 0).sort((a, b) => b.amount.diff - a.amount.diff).slice(0, 3);
    const azalan = rows.filter((r) => (r.amount.diff || 0) < 0).sort((a, b) => a.amount.diff - b.amount.diff).slice(0, 3);
    const p = [];
    p.push(
      `ERP net satışı (KDV hariç, iadeler düşülmüş) ${money(d.netSales.total.cur)} — önceki haftaya göre `
      + `${chgTxt(d.netSales.total.chg)}. Bu rakam yukarıdaki GA4 cirosuyla aynı şey değildir; iki tanım birbirine eşitlenmemeli.`,
    );
    if (artan.length) {
      p.push(`Yükselenler: ${artan.map((r) => `${r.brand} ${money(r.amount.cur)} (${fark(r.amount.diff)}, ${chgTxt(r.amount.chg)})`).join(', ')}.`);
    }
    if (azalan.length) {
      p.push(`Gerileyenler: ${azalan.map((r) => `${r.brand} ${money(r.amount.cur)} (${fark(r.amount.diff)}, ${chgTxt(r.amount.chg)})`).join(', ')}.`);
    }
    const ilk = rows[0];
    if (ilk) p.push(`Haftanın net satışının ${pctv(ilk.share)} kısmı tek başına ${ilk.brand} tarafından yapıldı.`);
    S.push({ id: 'net-satis', baslik: 'Marka bazında net satış', paragraflar: p, shot: 'net-satis', notYeri: 'markalar_net' });
  }

  return S;
}

export default buildProse;
